// Core React and rendering
import { createRoot } from "react-dom/client";
import { StrictMode } from "react";
// StrictMode helps detect potential issues in the application during development.
// It does not affect production builds.
// Issues such as unsafe lifecycle methods, deprecated APIs, and unexpected side effects can be highlighted when StrictMode is enabled.

// Styling
import "./index.css";

// App components and providers
import App from "./App.tsx";
import { PrimeReactProvider } from "primereact/api";
import { cncStore } from "./store/store.ts";
import { registerApiStoreDispatch } from "./services/apiClient";

// Authentication
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";

import { Provider } from "react-redux";

// Version check (runs on module load)
import "./shared/utils/versionCheck.ts";
import ToastProvider from "./contexts/ToastContext.tsx";
import { msalConfig } from "./config/auth/authConfig.ts";
import { initializeTheme } from "./config/theme.ts";
import { ThemeProvider } from "./contexts/ThemeContext.tsx";

const msalInstance = new PublicClientApplication(msalConfig);
registerApiStoreDispatch(cncStore.dispatch);
initializeTheme();

async function renderApp() {
  try {
    await msalInstance.initialize();
    const response = await msalInstance.handleRedirectPromise();
    if (response?.account) {
      msalInstance.setActiveAccount(response.account);
    }

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      // Use redirect instead of popup
      const loginRequest = {
        scopes: ["User.Read"],
      };
      msalInstance.loginRedirect(loginRequest); // No await needed
      return; // Don't render app yet
    }

    // Only render if user is logged in
    createRoot(document.getElementById("root")!).render(
      <StrictMode>
        <Provider store={cncStore}>
          <ThemeProvider>
            <ToastProvider position="bottom-right" max={20}>
              <MsalProvider instance={msalInstance}>
                <PrimeReactProvider value={{ unstyled: false }}>
                  <App />
                </PrimeReactProvider>
              </MsalProvider>
            </ToastProvider>
          </ThemeProvider>
        </Provider>
      </StrictMode>,
    );
  } catch (error) {
    console.error("Failed to initialize MSAL or render app:", error);
    createRoot(document.getElementById("root")!).render(
      <div>CNC failed to load. Please refresh or contact support.</div>,
    );
  }
}

renderApp();

